/**
 * FlowTestcaseGeneratorPage Component
 * 
 * 화면 흐름 기반 테스트케이스 자동 생성 페이지
 * 스크린샷 + 전환 관계 데이터를 입력받아 5종의 CSV를 자동 생성합니다.
 */

import { useState, useCallback } from 'react';
import { useLanguage } from '../i18n';
import { useAuth } from '../auth';
import {
  generateFlowCSV,
  downloadFlowCSVZip,
  type FlowScreenInput,
  type FlowTransitionInput,
  type FlowTestcaseGeneratorInput,
  type FlowTestcaseGeneratorResponse,
} from '../api';

interface FlowTestcaseGeneratorPageProps {
  onBack: () => void;
}

type TabType = 'input' | 'preview' | 'download';

export default function FlowTestcaseGeneratorPage({ onBack }: FlowTestcaseGeneratorPageProps) {
  const { t, lang } = useLanguage();
  const { isAuthenticated } = useAuth();
  
  // 상태
  const [activeTab, setActiveTab] = useState<TabType>('input');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FlowTestcaseGeneratorResponse | null>(null);
  
  // 입력 데이터
  const [screens, setScreens] = useState<FlowScreenInput[]>([
    { id: 'home', name: lang === 'ko' ? '홈 화면' : 'Home', is_menu: true },
  ]);
  const [transitions, setTransitions] = useState<FlowTransitionInput[]>([]);
  const [testidPrefix, setTestidPrefix] = useState('');
  const [autoGenerateTestids, setAutoGenerateTestids] = useState(true);
  const [autoGenerateAssertions, setAutoGenerateAssertions] = useState(true);
  
  // CSV 미리보기 탭
  const [previewTab, setPreviewTab] = useState<string>('screens');
  
  // 화면 추가
  const addScreen = useCallback(() => {
    const newId = `screen_${screens.length + 1}`;
    setScreens([...screens, { 
      id: newId, 
      name: lang === 'ko' ? `화면 ${screens.length + 1}` : `Screen ${screens.length + 1}`,
      is_menu: false 
    }]);
  }, [screens, lang]);
  
  // 화면 제거
  const removeScreen = useCallback((index: number) => {
    if (screens.length <= 1) return;
    const newScreens = screens.filter((_, i) => i !== index);
    setScreens(newScreens);
  }, [screens]);
  
  // 화면 업데이트
  const updateScreen = useCallback((index: number, field: keyof FlowScreenInput, value: string | boolean) => {
    const newScreens = [...screens];
    newScreens[index] = { ...newScreens[index], [field]: value };
    setScreens(newScreens);
  }, [screens]);
  
  // 전환 추가
  const addTransition = useCallback(() => {
    if (screens.length < 2) {
      setError(lang === 'ko' ? '전환을 추가하려면 최소 2개의 화면이 필요합니다.' : 'At least 2 screens are required to add a transition.');
      return;
    }
    setTransitions([...transitions, {
      from_screen: screens[0].id,
      to_screen: screens[1]?.id || screens[0].id,
      trigger_label: lang === 'ko' ? '버튼 클릭' : 'Button Click',
    }]);
  }, [transitions, screens, lang]);
  
  // 전환 제거
  const removeTransition = useCallback((index: number) => {
    setTransitions(transitions.filter((_, i) => i !== index));
  }, [transitions]);
  
  // 전환 업데이트
  const updateTransition = useCallback((index: number, field: keyof FlowTransitionInput, value: string) => {
    const newTransitions = [...transitions];
    newTransitions[index] = { ...newTransitions[index], [field]: value };
    setTransitions(newTransitions);
  }, [transitions]);
  
  // CSV 생성
  const handleGenerate = useCallback(async () => {
    if (screens.length === 0) {
      setError(lang === 'ko' ? '최소 1개의 화면을 입력해주세요.' : 'Please add at least 1 screen.');
      return;
    }
    if (transitions.length === 0) {
      setError(lang === 'ko' ? '최소 1개의 전환을 입력해주세요.' : 'Please add at least 1 transition.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const input: FlowTestcaseGeneratorInput = {
        screens,
        transitions,
        testid_prefix: testidPrefix,
        auto_generate_testids: autoGenerateTestids,
        auto_generate_assertions: autoGenerateAssertions,
        lang,
      };
      
      const response = await generateFlowCSV(input);
      setResult(response);
      setActiveTab('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [screens, transitions, testidPrefix, autoGenerateTestids, autoGenerateAssertions, lang]);
  
  // ZIP 다운로드
  const handleDownload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const input: FlowTestcaseGeneratorInput = {
        screens,
        transitions,
        testid_prefix: testidPrefix,
        auto_generate_testids: autoGenerateTestids,
        auto_generate_assertions: autoGenerateAssertions,
        lang,
      };
      
      const blob = await downloadFlowCSVZip(input);
      
      // 다운로드
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'flow_testcases.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [screens, transitions, testidPrefix, autoGenerateTestids, autoGenerateAssertions, lang]);
  
  // 단일 CSV 다운로드
  const handleDownloadSingleCSV = useCallback((csvName: string, content: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csvName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, []);
  
  // JSON 임포트
  const handleImportJSON = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        if (data.screens && Array.isArray(data.screens)) {
          setScreens(data.screens);
        }
        if (data.transitions && Array.isArray(data.transitions)) {
          setTransitions(data.transitions);
        }
        if (data.testid_prefix) {
          setTestidPrefix(data.testid_prefix);
        }
      } catch {
        setError(lang === 'ko' ? 'JSON 파싱 오류' : 'JSON parsing error');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [lang]);
  
  // JSON 내보내기
  const handleExportJSON = useCallback(() => {
    const data = {
      screens,
      transitions,
      testid_prefix: testidPrefix,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'flow_data.json';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, [screens, transitions, testidPrefix]);
  
  // 비로그인 시
  if (!isAuthenticated) {
    return (
      <div className="neo-card-static bg-[#1e2a3a] p-8 text-center" data-testid="flow-generator-login-required">
        <h2 className="text-xl font-bold text-terminal-text mb-4">
          {t.auth?.loginRequired || '로그인이 필요합니다'}
        </h2>
        <button
          onClick={onBack}
          className="neo-btn bg-white text-black px-6 py-3 mx-auto"
        >
          {t.dashboard?.backToHome || '홈으로'}
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4 md:space-y-6" data-testid="flow-generator-page">
      {/* 헤더 */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="neo-btn bg-white dark:bg-[var(--neo-blue)] text-black text-sm px-3 py-1.5"
            data-testid="flow-generator-back-btn"
          >
            {t.dashboard?.backToHome || '← 뒤로'}
          </button>
          <h1 className="text-lg md:text-xl font-black text-black dark:text-white uppercase">
            {t.flowGenerator?.title || '🔄 Flow Testcase Generator'}
          </h1>
        </div>
        
        <div className="flex gap-2">
          <label className="neo-btn bg-white dark:bg-[var(--neo-blue)] text-black px-3 py-2 text-sm cursor-pointer">
            <span>📥 {lang === 'ko' ? 'JSON 가져오기' : 'Import JSON'}</span>
            <input
              type="file"
              accept=".json"
              onChange={handleImportJSON}
              className="hidden"
            />
          </label>
          <button
            onClick={handleExportJSON}
            className="neo-btn bg-white dark:bg-[var(--neo-blue)] text-black px-3 py-2 text-sm"
          >
            📤 {lang === 'ko' ? 'JSON 내보내기' : 'Export JSON'}
          </button>
        </div>
      </div>
      
      {/* 안내 배너 */}
      <div className="neo-card-static bg-gradient-to-r from-[var(--neo-blue)] to-[var(--neo-mint)] p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <h3 className="text-sm font-bold text-black">
              {lang === 'ko' ? '화면 흐름 기반 테스트케이스 자동 생성' : 'Auto-generate testcases from screen flow'}
            </h3>
            <p className="text-xs text-black/70 mt-1">
              {lang === 'ko' 
                ? '화면 목록과 전환 관계를 입력하면 screens, transitions, assertions, contexts, testcases CSV를 자동으로 생성합니다.' 
                : 'Enter screens and transitions to auto-generate CSV files for screens, transitions, assertions, contexts, and testcases.'}
            </p>
          </div>
        </div>
      </div>
      
      {/* 탭 */}
      <div className="flex gap-2 border-b-2 border-terminal-border pb-2">
        <button
          onClick={() => setActiveTab('input')}
          className={`px-4 py-2 text-sm font-bold transition-colors ${
            activeTab === 'input'
              ? 'text-[var(--neo-mint)] border-b-2 border-[var(--neo-mint)] -mb-[2px]'
              : 'text-terminal-muted hover:text-terminal-text'
          }`}
        >
          📝 {lang === 'ko' ? '입력' : 'Input'}
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          disabled={!result}
          className={`px-4 py-2 text-sm font-bold transition-colors ${
            activeTab === 'preview'
              ? 'text-[var(--neo-mint)] border-b-2 border-[var(--neo-mint)] -mb-[2px]'
              : 'text-terminal-muted hover:text-terminal-text disabled:opacity-50'
          }`}
        >
          👁️ {lang === 'ko' ? '미리보기' : 'Preview'}
        </button>
        <button
          onClick={() => setActiveTab('download')}
          disabled={!result}
          className={`px-4 py-2 text-sm font-bold transition-colors ${
            activeTab === 'download'
              ? 'text-[var(--neo-mint)] border-b-2 border-[var(--neo-mint)] -mb-[2px]'
              : 'text-terminal-muted hover:text-terminal-text disabled:opacity-50'
          }`}
        >
          📥 {lang === 'ko' ? '다운로드' : 'Download'}
        </button>
      </div>
      
      {/* 에러 메시지 */}
      {error && (
        <div className="neo-card-static bg-status-danger/20 border-2 border-status-danger p-3">
          <p className="text-sm text-status-danger">❌ {error}</p>
        </div>
      )}
      
      {/* 입력 탭 */}
      {activeTab === 'input' && (
        <div className="space-y-6">
          {/* 화면 목록 */}
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-terminal-text">
                📱 {lang === 'ko' ? '화면 목록' : 'Screens'} ({screens.length})
              </h3>
              <button
                onClick={addScreen}
                className="neo-btn bg-[var(--neo-mint)] text-black px-3 py-1.5 text-xs"
              >
                + {lang === 'ko' ? '화면 추가' : 'Add Screen'}
              </button>
            </div>
            
            <div className="space-y-3">
              {screens.map((screen, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-terminal-input border border-terminal-border rounded">
                  <input
                    type="text"
                    value={screen.id}
                    onChange={(e) => updateScreen(index, 'id', e.target.value)}
                    placeholder="ID"
                    className="w-24 px-2 py-1.5 bg-transparent border-b border-terminal-border text-xs text-terminal-text focus:outline-none focus:border-[var(--neo-mint)]"
                  />
                  <input
                    type="text"
                    value={screen.name}
                    onChange={(e) => updateScreen(index, 'name', e.target.value)}
                    placeholder={lang === 'ko' ? '화면 이름' : 'Screen Name'}
                    className="flex-1 px-2 py-1.5 bg-transparent border-b border-terminal-border text-xs text-terminal-text focus:outline-none focus:border-[var(--neo-mint)]"
                  />
                  <label className="flex items-center gap-1.5 text-xs text-terminal-muted cursor-pointer">
                    <input
                      type="checkbox"
                      checked={screen.is_menu || false}
                      onChange={(e) => updateScreen(index, 'is_menu', e.target.checked)}
                      className="w-4 h-4"
                    />
                    {lang === 'ko' ? '메뉴' : 'Menu'}
                  </label>
                  <button
                    onClick={() => removeScreen(index)}
                    disabled={screens.length <= 1}
                    className="text-terminal-muted hover:text-status-danger disabled:opacity-30"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          {/* 전환 관계 */}
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-terminal-text">
                🔄 {lang === 'ko' ? '전환 관계' : 'Transitions'} ({transitions.length})
              </h3>
              <button
                onClick={addTransition}
                className="neo-btn bg-[var(--neo-mint)] text-black px-3 py-1.5 text-xs"
              >
                + {lang === 'ko' ? '전환 추가' : 'Add Transition'}
              </button>
            </div>
            
            {transitions.length === 0 ? (
              <p className="text-xs text-terminal-muted text-center py-4">
                {lang === 'ko' ? '전환 관계를 추가해주세요' : 'Add transitions between screens'}
              </p>
            ) : (
              <div className="space-y-3">
                {transitions.map((transition, index) => (
                  <div key={index} className="flex flex-wrap items-center gap-2 p-3 bg-terminal-input border border-terminal-border rounded">
                    <select
                      value={transition.from_screen}
                      onChange={(e) => updateTransition(index, 'from_screen', e.target.value)}
                      className="px-2 py-1.5 bg-terminal-input border border-terminal-border text-xs text-terminal-text rounded"
                    >
                      {screens.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <span className="text-terminal-muted">→</span>
                    <input
                      type="text"
                      value={transition.trigger_label}
                      onChange={(e) => updateTransition(index, 'trigger_label', e.target.value)}
                      placeholder={lang === 'ko' ? '트리거 (버튼/링크)' : 'Trigger (button/link)'}
                      className="flex-1 min-w-[120px] px-2 py-1.5 bg-transparent border-b border-terminal-border text-xs text-terminal-text focus:outline-none focus:border-[var(--neo-mint)]"
                    />
                    <span className="text-terminal-muted">→</span>
                    <select
                      value={transition.to_screen}
                      onChange={(e) => updateTransition(index, 'to_screen', e.target.value)}
                      className="px-2 py-1.5 bg-terminal-input border border-terminal-border text-xs text-terminal-text rounded"
                    >
                      {screens.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeTransition(index)}
                      className="text-terminal-muted hover:text-status-danger"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* 옵션 */}
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <h3 className="text-sm font-bold text-terminal-text mb-4">
              ⚙️ {lang === 'ko' ? '옵션' : 'Options'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-terminal-muted mb-1.5">
                  data-testid {lang === 'ko' ? '접두사' : 'Prefix'}
                </label>
                <input
                  type="text"
                  value={testidPrefix}
                  onChange={(e) => setTestidPrefix(e.target.value)}
                  placeholder={lang === 'ko' ? '예: app-' : 'e.g., app-'}
                  className="w-full max-w-xs px-3 py-2 bg-terminal-input border-2 border-terminal-border text-sm text-terminal-text focus:outline-none focus:border-[var(--neo-mint)]"
                />
              </div>
              
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-terminal-text cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoGenerateTestids}
                    onChange={(e) => setAutoGenerateTestids(e.target.checked)}
                    className="w-4 h-4"
                  />
                  {lang === 'ko' ? 'testid 자동 생성' : 'Auto-generate testids'}
                </label>
                <label className="flex items-center gap-2 text-sm text-terminal-text cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoGenerateAssertions}
                    onChange={(e) => setAutoGenerateAssertions(e.target.checked)}
                    className="w-4 h-4"
                  />
                  {lang === 'ko' ? 'assertions 자동 생성' : 'Auto-generate assertions'}
                </label>
              </div>
            </div>
          </div>
          
          {/* 생성 버튼 */}
          <div className="flex justify-center">
            <button
              onClick={handleGenerate}
              disabled={isLoading || screens.length === 0 || transitions.length === 0}
              className="neo-btn bg-[var(--neo-mint)] text-black px-8 py-3 text-sm font-bold disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  {lang === 'ko' ? '생성 중...' : 'Generating...'}
                </span>
              ) : (
                <span>🚀 {lang === 'ko' ? 'CSV 생성하기' : 'Generate CSV'}</span>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* 미리보기 탭 */}
      {activeTab === 'preview' && result && (
        <div className="space-y-4">
          {/* 통계 */}
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <h3 className="text-sm font-bold text-terminal-text mb-3">📊 {lang === 'ko' ? '통계' : 'Stats'}</h3>
            <div className="flex flex-wrap gap-4 text-xs">
              <span className="px-3 py-1.5 bg-[var(--neo-blue)]/20 rounded">
                📱 {lang === 'ko' ? '화면' : 'Screens'}: {result.stats.total_screens as number}
              </span>
              <span className="px-3 py-1.5 bg-[var(--neo-mint)]/20 rounded">
                🔄 {lang === 'ko' ? '전환' : 'Transitions'}: {result.stats.total_transitions as number}
              </span>
              <span className="px-3 py-1.5 bg-[var(--neo-orange)]/20 rounded">
                📋 {lang === 'ko' ? '테스트케이스' : 'Testcases'}: {result.stats.total_testcases as number}
              </span>
            </div>
            
            {result.warnings.length > 0 && (
              <div className="mt-3 p-2 bg-status-warning/10 rounded">
                <p className="text-xs text-status-warning font-bold mb-1">⚠️ {lang === 'ko' ? '경고' : 'Warnings'}</p>
                <ul className="text-xs text-terminal-muted list-disc list-inside">
                  {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>
          
          {/* CSV 미리보기 탭 */}
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-4">
            <div className="flex gap-2 border-b border-terminal-border pb-2 mb-4 overflow-x-auto">
              {['screens', 'transitions', 'menu_nodes', 'assertions', 'contexts', 'testcases'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setPreviewTab(tab)}
                  className={`px-3 py-1.5 text-xs font-bold whitespace-nowrap transition-colors ${
                    previewTab === tab
                      ? 'text-[var(--neo-mint)] border-b-2 border-[var(--neo-mint)] -mb-[2px]'
                      : 'text-terminal-muted hover:text-terminal-text'
                  }`}
                >
                  {tab}.csv
                </button>
              ))}
            </div>
            
            <div className="relative">
              <button
                onClick={() => {
                  const csvContent = result.csv_outputs[`${previewTab}_csv` as keyof typeof result.csv_outputs];
                  handleDownloadSingleCSV(`${previewTab}.csv`, csvContent);
                }}
                className="absolute top-2 right-2 neo-btn bg-[var(--neo-blue)] text-black px-2 py-1 text-[10px]"
              >
                📥 {lang === 'ko' ? '다운로드' : 'Download'}
              </button>
              
              <pre className="p-4 bg-terminal-bg border border-terminal-border rounded text-xs text-terminal-text overflow-x-auto max-h-[400px]">
                {result.csv_outputs[`${previewTab}_csv` as keyof typeof result.csv_outputs]}
              </pre>
            </div>
          </div>
        </div>
      )}
      
      {/* 다운로드 탭 */}
      {activeTab === 'download' && result && (
        <div className="space-y-4">
          <div className="neo-card-static bg-white dark:bg-[#1e2a3a] p-6 text-center">
            <span className="text-5xl mb-4 block">📦</span>
            <h3 className="text-lg font-bold text-terminal-text mb-2">
              {lang === 'ko' ? '모든 CSV 다운로드' : 'Download All CSVs'}
            </h3>
            <p className="text-sm text-terminal-muted mb-6">
              {lang === 'ko' 
                ? '6개의 CSV 파일을 ZIP으로 다운로드합니다.' 
                : 'Download all 6 CSV files as a ZIP archive.'}
            </p>
            
            <button
              onClick={handleDownload}
              disabled={isLoading}
              className="neo-btn bg-[var(--neo-mint)] text-black px-8 py-3 text-sm font-bold disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  {lang === 'ko' ? '다운로드 중...' : 'Downloading...'}
                </span>
              ) : (
                <span>📥 {lang === 'ko' ? 'ZIP 다운로드' : 'Download ZIP'}</span>
              )}
            </button>
            
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
              {['screens', 'transitions', 'menu_nodes', 'assertions', 'contexts', 'testcases'].map((name) => (
                <button
                  key={name}
                  onClick={() => {
                    const csvContent = result.csv_outputs[`${name}_csv` as keyof typeof result.csv_outputs];
                    handleDownloadSingleCSV(`${name}.csv`, csvContent);
                  }}
                  className="neo-btn bg-white dark:bg-terminal-input text-black dark:text-white border-2 border-terminal-border px-3 py-2 text-xs"
                >
                  📄 {name}.csv
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
